import { PaperSize, Orientation, PrintScale, MarginSize, PAPER_DIMENSIONS, MARGIN_VALUES } from './paperConfig'

const UNBREAKABLE_TYPES = new Set(['graph', 'pie', 'infobox'])
const MIN_ROWS_PER_PAGE = 1

export interface PageBreak {
  y: number
  pageNumber: number
  isForced: boolean
  element: HTMLElement
}

export interface SectionGeometry {
  id: number
  type: string
  top: number
  bottom: number
  element: HTMLElement
  rowYs: { y: number; element: HTMLElement }[]
}

// ── Measurement ──

export const measureSections = (contentEl: HTMLElement, zoom: number): SectionGeometry[] => {
  const contentTop = contentEl.getBoundingClientRect().top / zoom

  return [...contentEl.querySelectorAll<HTMLElement>('[data-section-id]')].map(el => {
    const r = el.getBoundingClientRect()
    const type = el.dataset.sectionType || ''
    const rowYs: { y: number; element: HTMLElement }[] = []
    if (type === 'table') {
      const seen = new Set<Element>()
      el.querySelectorAll('.ag-center-cols-container .ag-row').forEach(row => {
        if (seen.has(row)) return
        seen.add(row)
        rowYs.push({ y: row.getBoundingClientRect().top / zoom - contentTop, element: row as HTMLElement })
      })
      rowYs.sort((a, b) => a.y - b.y)
    } else if (type === 'text') {
      el.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, table').forEach(child => {
        rowYs.push({ y: child.getBoundingClientRect().top / zoom - contentTop, element: child })
      })
      rowYs.sort((a, b) => a.y - b.y)
    }

    return {
      id: Number(el.dataset.sectionId),
      type,
      top: r.top / zoom - contentTop,
      bottom: r.bottom / zoom - contentTop,
      element: el,
      rowYs
    }
  })
}

// ── Break candidates ──

interface SnapPoint {
  y: number
  element: HTMLElement
  forced: boolean
}

const collectSnapPoints = (sections: SectionGeometry[], forcedIds: Set<number>, pageHeight: number): SnapPoint[] => {
  const points = new Map<number, SnapPoint>()
  const add = (y: number, element: HTMLElement, forced: boolean) => {
    const key = Math.round(y)
    const existing = points.get(key)
    if (!existing || (forced && !existing.forced)) {
      points.set(key, { y: key, element, forced })
    }
  }

  sections.forEach(s => {
    add(s.top, s.element, forcedIds.has(s.id))

    if (UNBREAKABLE_TYPES.has(s.type)) return

    s.rowYs.forEach((r, ri) => {
      if (ri < MIN_ROWS_PER_PAGE) return
      if (s.rowYs.length - ri < MIN_ROWS_PER_PAGE) return
      add(r.y, r.element, false)
    })
  })

  return [...points.values()].sort((a, b) => a.y - b.y)
}

// ── Page computation ──

const snapToNearest = (target: number, snapPoints: SnapPoint[], pageStart: number): SnapPoint | null => {
  const inRange = snapPoints.filter(p => p.y > pageStart && p.y <= target)
  if (inRange.length === 0) return null
  return inRange[inRange.length - 1]
}

const findPageBreak = (snapPoints: SnapPoint[], pageStart: number, pageHeight: number): SnapPoint | null => {
  const target = pageStart + pageHeight

  const forcedInRange = snapPoints.find(p => p.forced && p.y > pageStart && p.y <= target)
  if (forcedInRange) return forcedInRange

  return snapToNearest(target, snapPoints, pageStart)
}

const getSectionId = (el: HTMLElement): number =>
  Number(el.closest('[data-section-id]')?.getAttribute('data-section-id') ?? 0)

export const computeBreaks = (
  sections: SectionGeometry[],
  pageHeight: number,
  totalHeight: number,
  forcedIds: Set<number>
): PageBreak[] => {
  const snapPoints = collectSnapPoints(sections, forcedIds, pageHeight)
  const results: SnapPoint[] = []
  let cursor = 0

  while (cursor + pageHeight < totalHeight) {
    const result = findPageBreak(snapPoints, cursor, pageHeight)
    if (result) {
      results.push(result)
      cursor = result.y
    } else {
      cursor += pageHeight
    }
  }

  snapPoints.filter(p => p.forced && p.y > cursor).forEach(p => results.push(p))

  return results.map((r, i) => ({
    y: r.y,
    pageNumber: i + 2,
    isForced: forcedIds.has(getSectionId(r.element)),
    element: r.element
  }))
}

// ── Print DOM manipulation ──

export const insertPrintBreaks = (breaks: PageBreak[]): void => {
  breaks.forEach(b => {
    b.element.classList.add('print-page-break')
    Object.assign(b.element.style, { breakBefore: 'page' })
  })
}

export const clearPrintBreaks = (): void => {
  document.querySelectorAll<HTMLElement>('.print-page-break').forEach(element => {
    Object.assign(element.style, { breakBefore: '' })
    element.classList.remove('print-page-break')
  })
}

// ── Calculate & apply ──

interface CalculatePageBreaksOptions {
  contentEl: HTMLElement
  paperSize: PaperSize
  orientation: Orientation
  scale: PrintScale
  margin: MarginSize
  forcedBreakSectionIds: Set<number>
}

export const calculatePageBreaks = ({
  contentEl,
  paperSize,
  orientation,
  scale,
  margin,
  forcedBreakSectionIds
}: CalculatePageBreaksOptions): PageBreak[] => {
  const zoom = scale / 100
  const dim = PAPER_DIMENSIONS[paperSize]
  const w = orientation === 'portrait' ? dim.width : dim.height
  const h = orientation === 'portrait' ? dim.height : dim.width
  const marginMm = MARGIN_VALUES[margin]
  const contentWidthMm = w - 2 * marginMm
  const contentHeightMm = h - 2 * marginMm

  const contentWidthPx = contentEl.getBoundingClientRect().width / zoom
  const pageHeightPx = contentHeightMm * (contentWidthPx / contentWidthMm)
  const totalHeightPx = contentEl.getBoundingClientRect().height / zoom

  const sections = measureSections(contentEl, zoom)
  return computeBreaks(sections, pageHeightPx, totalHeightPx, forcedBreakSectionIds)
}
